/**
 * EditMemberSheet — slide-in sheet for editing an org member.
 *
 * Polish Wave 6 P2 — AC-Users-3 + AC-Users-4
 *
 * Built on FormSheet (sheet-migration-design-contract §4).
 *
 * Sheet body sections:
 *   - Header (read-only): avatar, display name, email
 *   - Details (read-only): joined date
 *   - Roles editor: MultiSelect of available org roles
 *   - Status toggle: Active / Inactive
 *   - Danger zone: Set Password (opens stacked Modal), Remove user (opens confirm Modal)
 *
 * Save button is disabled until at least one field differs from the baseline snapshot.
 *
 * Stacking rule (design-contract R2): destructive confirm modals open ON TOP of
 * the sheet (Modal z-index > Sheet z-index). The sheet stays open behind them.
 *
 * AC-Users-5 compliance: this component calls the existing org-scoped endpoints
 * (role assignment via RolesService, active toggle via OrgService, remove via
 * OrgService). All of those endpoints pull orgId from the authenticated session —
 * nothing is accepted from the request body.
 */

import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FormSheet } from '@/components/ui/FormSheet/FormSheet';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { MultiSelect } from '@/components/ui/MultiSelect/MultiSelect';
import { Modal } from '@/components/ui/Modal/Modal';
import { SetMemberPasswordModal } from '@/components/organization/SetMemberPasswordModal';
import { RolesService } from '@/services/RolesService';
import { useRemoveMemberMutation, useSetMemberActiveMutation } from '@/hooks/queries';
import { showToast } from '@/components/ui/Toast';
import { formatDate } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';
import { KeyRound, UserMinus, AlertTriangle } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EditMemberSheetProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  userId: string;
  userEmail: string;
  userName?: string;
  avatarUrl?: string | null;
  joinedAt?: string;
  /** The member's current active state when the sheet opens */
  isActive: boolean;
  /** The member's legacy role label (owner/admin/member) — used to gate destructive actions */
  roleLabel?: string;
}

interface FormSnapshot {
  roleIds: string[];
  isActive: boolean;
}

function snapshotDiff(a: FormSnapshot, b: FormSnapshot): Partial<Record<keyof FormSnapshot, boolean>> {
  return {
    roleIds: JSON.stringify(a.roleIds.slice().sort()) !== JSON.stringify(b.roleIds.slice().sort()),
    isActive: a.isActive !== b.isActive,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EditMemberSheet({
  isOpen,
  onClose,
  orgId,
  userId,
  userEmail,
  userName,
  avatarUrl,
  joinedAt,
  isActive: initialIsActive,
  roleLabel,
}: EditMemberSheetProps) {
  const queryClient = useQueryClient();
  const rolesService = new RolesService();

  // ── Password modal ──────────────────────────────────────────────────────
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // ── Remove confirm modal ────────────────────────────────────────────────
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // ── Form state ──────────────────────────────────────────────────────────
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isActiveState, setIsActiveState] = useState<boolean>(initialIsActive);

  // Baseline snapshot — captured once roles are loaded
  const [baseline, setBaseline] = useState<FormSnapshot | null>(null);

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: () => rolesService.getRoles(),
    enabled: isOpen && !!orgId,
  });

  const { data: userPermissionsData, isLoading: userRolesLoading } = useQuery({
    queryKey: ['userPermissions', orgId, userId],
    queryFn: () => rolesService.getUserPermissions(orgId, userId),
    enabled: isOpen && !!orgId && !!userId,
  });

  // Seed form state when user's current roles arrive
  useEffect(() => {
    if (!isOpen) return;
    if (userPermissionsData?.roles) {
      const ids = (userPermissionsData.roles as Array<{ id: string }>).map((r) => r.id);
      setSelectedRoleIds(ids);
      setIsActiveState(initialIsActive);
      setBaseline({ roleIds: ids, isActive: initialIsActive });
    }
  }, [isOpen, userPermissionsData, initialIsActive]);

  // Reset on close so next open starts fresh
  useEffect(() => {
    if (!isOpen) {
      setSelectedRoleIds([]);
      setIsActiveState(initialIsActive);
      setBaseline(null);
      setShowPasswordModal(false);
      setShowRemoveConfirm(false);
    }
  }, [isOpen, initialIsActive]);

  // ── Mutations ────────────────────────────────────────────────────────────
  const setMemberActiveMutation = useSetMemberActiveMutation();
  const removeMemberMutation = useRemoveMemberMutation();

  const saveRolesMutation = useMutation({
    mutationFn: async (roleIds: string[]) => {
      await rolesService.assignRolesToUser(orgId, userId, roleIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['userPermissions', orgId, userId] });
    },
    onError: (error: unknown) => {
      showToast.error(error instanceof Error ? error.message : t('Failed to update roles'));
    },
  });

  const isSubmitting = saveRolesMutation.isPending || setMemberActiveMutation.isPending;

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!baseline) return;
    const diff = snapshotDiff(baseline, { roleIds: selectedRoleIds, isActive: isActiveState });

    const promises: Promise<unknown>[] = [];
    if (diff.roleIds) {
      promises.push(saveRolesMutation.mutateAsync(selectedRoleIds));
    }
    if (diff.isActive) {
      promises.push(setMemberActiveMutation.mutateAsync({ orgId, userId, isActive: isActiveState }));
    }

    try {
      await Promise.all(promises);
      showToast.success(t('Member updated successfully'));
      onClose();
    } catch {
      // individual mutations already show toasts
    }
  };

  // ── Remove handler ───────────────────────────────────────────────────────
  const handleRemoveConfirm = async () => {
    try {
      await removeMemberMutation.mutateAsync({ orgId, userId });
      showToast.success(t('Member removed'));
      setShowRemoveConfirm(false);
      onClose();
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : t('Failed to remove member'));
    }
  };

  // ── Derived state ────────────────────────────────────────────────────────
  const displayName = userName || userEmail;
  const isLoading = rolesLoading || userRolesLoading;
  const currentSnapshot: FormSnapshot = { roleIds: selectedRoleIds, isActive: isActiveState };
  const isOwner = roleLabel === 'owner';

  const roleOptions = availableRoles.map((role) => ({
    value: role.id,
    label: role.name,
    description: role.description || undefined,
  }));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <FormSheet<FormSnapshot>
        isOpen={isOpen}
        onClose={onClose}
        mode="edit"
        title={displayName}
        subtitle={userEmail}
        baseline={baseline}
        current={currentSnapshot}
        diff={snapshotDiff}
        onSubmit={handleSave}
        isSubmitting={isSubmitting}
        submitDisabled={isLoading}
        width="sm"
      >
        {/* ── Header card: avatar + meta ─────────────────────────── */}
        <div className="flex items-center gap-4 pb-4 mb-4 border-b border-border">
          <Avatar
            size="lg"
            src={avatarUrl || undefined}
            initials={displayName.charAt(0).toUpperCase()}
            name={displayName}
          />
          <div className="min-w-0">
            <div className="font-semibold text-foreground truncate">{displayName}</div>
            <div className="text-sm text-muted-foreground truncate">{userEmail}</div>
            {joinedAt && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('Joined')} {formatDate(new Date(joinedAt))}
              </div>
            )}
          </div>
        </div>

        {/* ── Roles editor ──────────────────────────────────────── */}
        <div className="space-y-1.5 mb-4">
          <label className="block text-sm font-medium text-foreground">
            {t('Roles')}
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            {t('Select one or more roles to assign to this member.')}
          </p>
          <MultiSelect
            options={roleOptions}
            value={selectedRoleIds}
            onChange={setSelectedRoleIds}
            placeholder={t('Select roles...')}
            disabled={isLoading}
          />
        </div>

        {/* ── Active / Inactive toggle ───────────────────────────── */}
        <div className="flex items-center justify-between py-3 px-0 mb-4 border-y border-border">
          <div>
            <div className="text-sm font-medium text-foreground">{t('Account active')}</div>
            <div className="text-xs text-muted-foreground">
              {isActiveState
                ? t('Member can access this organisation.')
                : t('Member is suspended and cannot access this organisation.')}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Badge variant={isActiveState ? 'success' : 'warning'}>
              {isActiveState ? t('Active') : t('Inactive')}
            </Badge>
            <Switch
              checked={isActiveState}
              onChange={(e) => setIsActiveState(e.target.checked)}
              disabled={isOwner}
              data-testid="edit-member-active-toggle"
            />
          </div>
        </div>

        {/* ── Danger zone ───────────────────────────────────────── */}
        {!isOwner && (
          <div className="rounded-lg border border-error-200 bg-error-50/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-error-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {t('Danger zone')}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-foreground">{t('Set password')}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('Override this member\'s password and sign them out.')}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<KeyRound className="h-4 w-4" />}
                  onClick={() => setShowPasswordModal(true)}
                >
                  {t('Set Password')}
                </Button>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-error-200">
                <div>
                  <div className="text-sm font-medium text-error-700">{t('Remove user')}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('Permanently remove this member from the organisation.')}
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<UserMinus className="h-4 w-4" />}
                  onClick={() => setShowRemoveConfirm(true)}
                  isLoading={removeMemberMutation.isPending}
                  data-testid="edit-member-remove-btn"
                >
                  {t('Remove')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </FormSheet>

      {/* ── Set Password Modal (stacked on sheet — R2) ─────────── */}
      {showPasswordModal && (
        <SetMemberPasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          orgId={orgId}
          userId={userId}
          userEmail={userEmail}
          userName={userName}
        />
      )}

      {/* ── Remove Confirm Modal (stacked on sheet — R2) ──────── */}
      {showRemoveConfirm && (
        <Modal
          isOpen={showRemoveConfirm}
          onClose={() => setShowRemoveConfirm(false)}
          title={t('Remove member')}
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              {t('Are you sure you want to remove')} <strong>{displayName}</strong>{' '}
              {t('from this organisation? This action cannot be undone.')}
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowRemoveConfirm(false)}
                disabled={removeMemberMutation.isPending}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleRemoveConfirm}
                isLoading={removeMemberMutation.isPending}
                data-testid="confirm-remove-member-btn"
              >
                {t('Remove member')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
